import { useDispatch, useSelector } from 'react-redux';
import { EmptyState, Icon, StandAloneLink, Surface } from '@vst/beam';
import { DateRange, Payment } from '@vst/beam-icons/icons';
import { Alert } from '@vst/beam-icons/illustrative-icons';
import { useTranslation } from 'react-i18next';

import { PayBillLink } from '@app/router/constants';
import { useNavigate } from '@app/hooks';
import { selectUser } from '@app/redux/auth';
import { selectBillingInfo } from '@app/redux/billingInfo';
import { Issuer, selectConfig } from '@app/redux/config';
import { setIsConfirmationModalOpen } from '@app/redux/updateBillCycle';
import { postBillingMessage } from '@app/redux/utils';
import { EventTypes } from '@app/types';

import { BadgeStatus } from '@mfe/shared/schema-types';

import { goToAutopay } from '../../navigation';
import Summary from './summary';
import SummaryLoading from './summary/loading';

import styles from './styles.module.scss';

const BillingSummary = (): JSX.Element | null => {
  const dispatch = useDispatch();
  const { t } = useTranslation('Error');

  const {
    billingInfo: { summaryInvoiceStatus },
    loading,
    billingLoading,
    errors: apiError,
  } = useSelector(selectBillingInfo);

  const {
    user: {
      auth: {
        tokenInfo: { idToken },
      },
    },
  } = useSelector(selectUser);

  const isPlatform = !idToken;

  const { goTo } = useNavigate();

  const config = useSelector(selectConfig);

  const warning = summaryInvoiceStatus === BadgeStatus.PastDue;
  const isSalesforce = config.issuer === Issuer.Salesforce;

  const handleChangePaymentMethod = () => {
    goToAutopay(dispatch);
  };

  const openBillCycleModal = () => {
    if (!isPlatform) {
      goTo(`/${PayBillLink.PayBill}/${PayBillLink.UpdateBillCycle}`);
    }

    dispatch(setIsConfirmationModalOpen(true));
  };

  const handleChangeBillDate = () => {
    dispatch(
      postBillingMessage({
        eventType: EventTypes.ChangeBillDate,
      })
    );

    openBillCycleModal();
  };

  const isLoading = loading || billingLoading;

  if (isLoading) {
    return <SummaryLoading />;
  }

  if (apiError) {
    return (
      <Surface className={styles['errorCard']}>
        <EmptyState icon={Alert} title={t('title')} description={t('message', { error: t('billingsummary') })} />
      </Surface>
    );
  }

  return (
    <Surface data-cy="billingSummary" variant="primary" className={styles['container']}>
      <Surface className={warning ? styles['cardWarning'] : styles['card']}>
        <Summary />

        <Links
          isSalesforce={isSalesforce}
          handleChangeBillDate={handleChangeBillDate}
          handleChangePaymentMethod={handleChangePaymentMethod}
        />
      </Surface>
    </Surface>
  );
};

interface Props {
  isSalesforce: boolean;
  handleChangePaymentMethod: () => void;
  handleChangeBillDate: () => void;
}

const Links = ({ handleChangePaymentMethod, handleChangeBillDate, isSalesforce }: Props) => {
  const { t } = useTranslation('Summary');

  return (
    <>
      <Surface className={styles['link']} data-cy="changePaymentLink-button">
        <Icon icon={Payment} mr="16px" color="gray_600" />
        <StandAloneLink
          data-cy="changePaymentLink"
          variant="secondary"
          disabled={isSalesforce}
          href={''}
          onClick={(e) => {
            e.preventDefault();
            if (isSalesforce) {
              return;
            }
            handleChangePaymentMethod();
          }}
          linkSize="large"
        >
          {t('changePaymentMethod')}
        </StandAloneLink>
      </Surface>

      <Surface className={styles['link']} data-cy="changeBillDateLink-button">
        <Icon icon={DateRange} mr="16px" color="gray_600" />

        <StandAloneLink
          data-cy="changeBillDateLink"
          variant="secondary"
          disabled={isSalesforce}
          href={''}
          onClick={(e) => {
            e.preventDefault();
            if (isSalesforce) {
              return;
            }
            handleChangeBillDate();
          }}
          linkSize="large"
        >
          {t('changeBillDate')}
        </StandAloneLink>
      </Surface>
    </>
  );
};

export default BillingSummary;
